/**
 * Backtest Engine — executes trading strategies on historical candle data.
 * Takes a strategy definition (conditions) and candles, returns trade list + stats.
 */
import * as formulas from '../indicators/formulas.js';

// Compute indicator values for the full candle array
function computeIndicator(candles, indicatorId, params) {
  const fn = formulas[indicatorId];
  if (!fn) return null;
  const result = fn(candles, params);
  // Normalize: formulas return different shapes
  if (Array.isArray(result)) return result; // already array of {time, value}
  // Object with named series (e.g., macd returns {macd, signal, histogram})
  return result;
}

function getSeriesValues(computed, seriesKey) {
  if (Array.isArray(computed)) return computed.map(d => d.value ?? d.close ?? null);
  if (computed && computed[seriesKey]) return computed[seriesKey].map(d => d.value ?? null);
  return null;
}

function getSource(candles, src) {
  switch (src) {
    case 'close': return candles.map(c => c.close);
    case 'open': return candles.map(c => c.open);
    case 'high': return candles.map(c => c.high);
    case 'low': return candles.map(c => c.low);
    case 'volume': return candles.map(c => c.volume);
    case 'hl2': return candles.map(c => (c.high + c.low) / 2);
    default: return candles.map(c => c.close);
  }
}

function evalCondition(condition, i, candles, cache) {
  const { left, operator, right } = condition;

  const resolveValue = (side) => {
    if (side.type === 'price') return getSource(candles, side.source || 'close')[i];
    if (side.type === 'number') return parseFloat(side.value);
    if (side.type === 'indicator') {
      const key = `${side.indicator}_${JSON.stringify(side.params)}`;
      if (!cache[key]) {
        cache[key] = computeIndicator(candles, side.indicator, side.params || {});
      }
      const vals = getSeriesValues(cache[key], side.series || 'line');
      return vals ? vals[i] : null;
    }
    return null;
  };

  const leftVal = resolveValue(left);
  const rightVal = resolveValue(right);
  if (leftVal == null || rightVal == null) return false;

  // For crossover/crossunder we need previous values
  if (operator === 'crossover' || operator === 'crossunder') {
    if (i < 1) return false;
    const prevLeft = resolveValue({ ...left }); // same value type
    // Actually need to compute at i-1
    const resolveAt = (side, idx) => {
      if (side.type === 'price') return getSource(candles, side.source || 'close')[idx];
      if (side.type === 'number') return parseFloat(side.value);
      if (side.type === 'indicator') {
        const key = `${side.indicator}_${JSON.stringify(side.params)}`;
        if (!cache[key]) cache[key] = computeIndicator(candles, side.indicator, side.params || {});
        const vals = getSeriesValues(cache[key], side.series || 'line');
        return vals ? vals[idx] : null;
      }
      return null;
    };
    const prevL = resolveAt(left, i - 1);
    const prevR = resolveAt(right, i - 1);
    if (prevL == null || prevR == null) return false;
    if (operator === 'crossover') return prevL <= prevR && leftVal > rightVal;
    if (operator === 'crossunder') return prevL >= prevR && leftVal < rightVal;
  }

  switch (operator) {
    case '>': return leftVal > rightVal;
    case '<': return leftVal < rightVal;
    case '>=': return leftVal >= rightVal;
    case '<=': return leftVal <= rightVal;
    case '==': return Math.abs(leftVal - rightVal) < 0.0001;
    default: return false;
  }
}

function evalConditionGroup(conditions, logic, i, candles, cache) {
  if (!conditions || conditions.length === 0) return false;
  if (logic === 'OR') return conditions.some(c => evalCondition(c, i, candles, cache));
  return conditions.every(c => evalCondition(c, i, candles, cache));
}

/**
 * Run backtest
 * @param {Object} strategy - { entryLong, entryShort, exitLong, exitShort, stopLossPct, takeProfitPct, trailingPct, logic, capital }
 * @param {Array} candles - [{time, open, high, low, close, volume}]
 * @returns {{ trades, stats, equityCurve }}
 */
export function runBacktest(strategy, candles) {
  const {
    entryLong = [], entryShort = [],
    exitLong = [], exitShort = [],
    stopLossPct = 0, takeProfitPct = 0, trailingPct = 0,
    logic = 'AND', capital = 10000,
  } = strategy;

  const cache = {};
  const trades = [];
  let position = null; // { direction, entryPrice, entryIndex, volume, sl, tp, trailHigh, trailLow }
  let equity = capital;
  const equityCurve = [{ index: 0, equity: capital, time: candles[0]?.time }];

  const warmup = 50; // skip first N bars for indicator warmup

  for (let i = warmup; i < candles.length; i++) {
    const bar = candles[i];
    const barTime = bar.time;

    // Check SL/TP on open position
    if (position) {
      // Trailing stop update
      if (trailingPct > 0 && position.direction === 'long') {
        if (bar.high > position.trailHigh) {
          position.trailHigh = bar.high;
          position.sl = position.trailHigh * (1 - trailingPct / 100);
        }
      }
      if (trailingPct > 0 && position.direction === 'short') {
        if (bar.low < position.trailLow) {
          position.trailLow = bar.low;
          position.sl = position.trailLow * (1 + trailingPct / 100);
        }
      }

      let exitPrice = null, exitReason = null;

      if (position.direction === 'long') {
        if (position.sl && bar.low <= position.sl) { exitPrice = position.sl; exitReason = 'stop_loss'; }
        else if (position.tp && bar.high >= position.tp) { exitPrice = position.tp; exitReason = 'take_profit'; }
        else if (exitLong.length > 0 && evalConditionGroup(exitLong, logic, i, candles, cache)) { exitPrice = bar.close; exitReason = 'signal'; }
      } else {
        if (position.sl && bar.high >= position.sl) { exitPrice = position.sl; exitReason = 'stop_loss'; }
        else if (position.tp && bar.low <= position.tp) { exitPrice = position.tp; exitReason = 'take_profit'; }
        else if (exitShort.length > 0 && evalConditionGroup(exitShort, logic, i, candles, cache)) { exitPrice = bar.close; exitReason = 'signal'; }
      }

      if (exitPrice) {
        const pnl = position.direction === 'long'
          ? (exitPrice - position.entryPrice) * position.volume
          : (position.entryPrice - exitPrice) * position.volume;
        equity += pnl;
        trades.push({
          direction: position.direction,
          entryPrice: position.entryPrice,
          exitPrice,
          entryTime: candles[position.entryIndex].time,
          exitTime: barTime,
          pnl: Math.round(pnl * 100) / 100,
          exitReason,
          bars: i - position.entryIndex,
        });
        position = null;
      }
    }

    // Check entry conditions (only if no position)
    if (!position) {
      if (entryLong.length > 0 && evalConditionGroup(entryLong, logic, i, candles, cache)) {
        const volume = 1;
        const ep = bar.close;
        position = {
          direction: 'long', entryPrice: ep, entryIndex: i, volume,
          sl: stopLossPct > 0 ? ep * (1 - stopLossPct / 100) : null,
          tp: takeProfitPct > 0 ? ep * (1 + takeProfitPct / 100) : null,
          trailHigh: ep, trailLow: ep,
        };
      } else if (entryShort.length > 0 && evalConditionGroup(entryShort, logic, i, candles, cache)) {
        const volume = 1;
        const ep = bar.close;
        position = {
          direction: 'short', entryPrice: ep, entryIndex: i, volume,
          sl: stopLossPct > 0 ? ep * (1 + stopLossPct / 100) : null,
          tp: takeProfitPct > 0 ? ep * (1 - takeProfitPct / 100) : null,
          trailHigh: ep, trailLow: ep,
        };
      }
    }

    equityCurve.push({ index: i, equity: Math.round(equity * 100) / 100, time: barTime });
  }

  // Close open position at end
  if (position) {
    const lastBar = candles[candles.length - 1];
    const pnl = position.direction === 'long'
      ? (lastBar.close - position.entryPrice) * position.volume
      : (position.entryPrice - lastBar.close) * position.volume;
    equity += pnl;
    trades.push({
      direction: position.direction,
      entryPrice: position.entryPrice,
      exitPrice: lastBar.close,
      entryTime: candles[position.entryIndex].time,
      exitTime: lastBar.time,
      pnl: Math.round(pnl * 100) / 100,
      exitReason: 'end',
      bars: candles.length - 1 - position.entryIndex,
    });
  }

  // Compute stats
  const wins = trades.filter(t => t.pnl > 0);
  const losses = trades.filter(t => t.pnl <= 0);
  const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
  const winRate = trades.length > 0 ? (wins.length / trades.length) * 100 : 0;
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + t.pnl, 0) / losses.length) : 0;
  const profitFactor = avgLoss > 0 ? (wins.reduce((s, t) => s + t.pnl, 0)) / Math.abs(losses.reduce((s, t) => s + t.pnl, 0) || 1) : Infinity;
  const expectancy = trades.length > 0 ? totalPnl / trades.length : 0;

  // Max drawdown
  let peak = capital, maxDD = 0;
  for (const pt of equityCurve) {
    if (pt.equity > peak) peak = pt.equity;
    const dd = peak - pt.equity;
    if (dd > maxDD) maxDD = dd;
  }

  // Buy & Hold comparison
  const bhReturn = candles.length > warmup
    ? ((candles[candles.length - 1].close - candles[warmup].close) / candles[warmup].close) * 100
    : 0;

  return {
    trades,
    equityCurve,
    stats: {
      totalTrades: trades.length,
      wins: wins.length,
      losses: losses.length,
      winRate: Math.round(winRate * 10) / 10,
      totalPnl: Math.round(totalPnl * 100) / 100,
      avgWin: Math.round(avgWin * 100) / 100,
      avgLoss: Math.round(avgLoss * 100) / 100,
      profitFactor: profitFactor === Infinity ? '∞' : Math.round(profitFactor * 100) / 100,
      expectancy: Math.round(expectancy * 100) / 100,
      maxDrawdown: Math.round(maxDD * 100) / 100,
      maxDrawdownPct: peak > 0 ? Math.round((maxDD / peak) * 10000) / 100 : 0,
      returnPct: Math.round(((equity - capital) / capital) * 10000) / 100,
      buyHoldReturn: Math.round(bhReturn * 100) / 100,
      avgBars: trades.length > 0 ? Math.round(trades.reduce((s, t) => s + t.bars, 0) / trades.length) : 0,
    },
  };
}
